import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CookieBanner from '../../components/feature/CookieBanner';
import { useSEO, getBreadcrumbSchema } from '../../utils/seo';

const FaqPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('todas');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const categories = [
    { id: 'todas', label: 'Todas', icon: 'ri-list-check' },
    { id: 'precio', label: 'Precio', icon: 'ri-money-euro-circle-line' },
    { id: 'proceso', label: 'Proceso', icon: 'ri-flow-chart' },
    { id: 'logistica', label: 'Logística', icon: 'ri-truck-line' },
    { id: 'tecnico', label: 'Técnico', icon: 'ri-tools-line' }
  ];

  const faqs = [
    {
      category: 'precio',
      question: '¿Cuánto cuesta el servicio de limpieza post mortem?',
      answer: 'El precio depende de varios factores: estado del inmueble, superficie afectada, tiempo transcurrido y servicios adicionales necesarios. Como orientación, una limpieza post mortem básica parte desde 450€, la desinfección desde 350€ y la neutralización de olores desde 280€. Ofrecemos presupuestos gratuitos y sin compromiso tras una valoración inicial. Podemos realizar la valoración por teléfono con fotografías o desplazarnos al inmueble si lo prefiere. El presupuesto se entrega siempre por escrito y detallado.'
    },
    {
      category: 'proceso',
      question: '¿Cuánto tiempo tarda la intervención?',
      answer: 'Los tiempos varían según el servicio: una limpieza post mortem completa suele requerir entre 4 y 8 horas, la desinfección entre 2 y 4 horas, y la neutralización de olores entre 3 y 6 horas. En casos de limpieza de choque, podemos completar la intervención en 6-12 horas de trabajo intensivo. Si se requiere vaciado de enseres, el proceso puede extenderse entre 1 y 3 días según el volumen. Siempre adaptamos los horarios a sus necesidades y podemos trabajar en fines de semana o festivos si la situación lo requiere.'
    },
    {
      category: 'logistica',
      question: '¿Cómo se realiza la valoración inicial?',
      answer: 'Ofrecemos dos opciones para la valoración: valoración telefónica con fotografías (más rápida, en menos de 2 horas) o valoración presencial en el inmueble (más precisa, en menos de 4 horas). En ambos casos, la valoración es gratuita y sin compromiso. Para la valoración telefónica, necesitamos fotografías del espacio afectado y algunos datos básicos (superficie, tiempo transcurrido, situación general). Tras la valoración, emitimos un presupuesto detallado por escrito que incluye todos los servicios necesarios y el desglose de costes.'
    },
    {
      category: 'logistica',
      question: '¿Garantizan la discreción del servicio?',
      answer: 'La discreción es una de nuestras prioridades absolutas. Nuestros vehículos no llevan ninguna identificación externa que indique el tipo de servicio. Adaptamos los horarios de intervención para minimizar coincidencias con vecinos si así lo desea. Accedemos al inmueble de forma discreta y todo nuestro equipo está formado en confidencialidad. No compartimos información sobre nuestras intervenciones con terceros salvo autorización expresa. Toda la documentación se maneja con absoluta confidencialidad y podemos coordinar con la portería o comunidad de forma discreta si es necesario.'
    },
    {
      category: 'tecnico',
      question: '¿Cómo eliminan los olores de forma definitiva?',
      answer: 'No enmascaramos los olores, los eliminamos mediante procesos químicos y físicos. Utilizamos tres técnicas principales: neutralizadores enzimáticos que destruyen las moléculas causantes del olor, ozonización profesional que oxida y elimina compuestos orgánicos, y ventilación controlada para renovar completamente el aire. El proceso de ozonización requiere que el espacio permanezca cerrado durante 2-4 horas mientras actúa el ozono, y posteriormente se ventila durante otras 2-4 horas. En casos de impregnación profunda en textiles o superficies porosas, aplicamos tratamientos específicos adicionales. Garantizamos la eliminación efectiva del olor.'
    },
    {
      category: 'tecnico',
      question: '¿Qué hacen con los residuos y enseres?',
      answer: 'Somos gestores autorizados de residuos y cumplimos toda la normativa de trazabilidad. Los residuos biológicos se gestionan según protocolo sanitario con documentación completa. Para los enseres, primero coordinamos con la familia para identificar objetos de valor sentimental o económico que deseen conservar. Los objetos a conservar se embalan de forma segura. Los enseres en buen estado que la familia no desee pueden donarse a entidades benéficas si lo autorizan. El resto se gestiona como residuo con la documentación de trazabilidad correspondiente. Entregamos toda la documentación de gestión de residuos al finalizar el servicio.'
    },
    {
      category: 'logistica',
      question: '¿Debo avisar a los vecinos o a la portería?',
      answer: 'No es obligatorio, pero puede ser recomendable en algunos casos. Nosotros podemos encargarnos de la comunicación con la portería o comunidad de vecinos si lo prefiere, siempre de forma discreta. Si hay que realizar trabajos que puedan generar algo de ruido (como vaciado de enseres) o si vamos a ocupar zonas comunes temporalmente, es conveniente avisar. En cualquier caso, trabajamos con la máxima discreción y respeto hacia el entorno. Podemos adaptar horarios para minimizar molestias y coordinamos el uso de ascensores o zonas comunes según las normas de la comunidad.'
    },
    {
      category: 'proceso',
      question: '¿Es necesario que esté presente algún familiar?',
      answer: 'No es imprescindible, pero puede ser conveniente en la fase inicial para entregarnos las llaves, mostrarnos el inmueble y aclarar cualquier duda. Durante la intervención no es necesaria su presencia, de hecho muchas familias prefieren no estar presentes por la carga emocional. Si se va a realizar vaciado de enseres, sí recomendamos una presencia inicial para identificar objetos de valor sentimental que deseen conservar, aunque también podemos hacer fotografías de todo y que decidan posteriormente. Al finalizar, realizamos una revisión conjunta si lo desean o simplemente entregamos las llaves y la documentación.'
    },
    {
      category: 'logistica',
      question: '¿Trabajan en fines de semana y festivos?',
      answer: 'Sí, estamos disponibles 24 horas al día, 7 días a la semana, incluidos fines de semana y festivos. Entendemos que estas situaciones no entienden de horarios y que a menudo hay plazos ajustados para la entrega del inmueble. Nuestro servicio de limpieza de choque está especialmente diseñado para respuestas urgentes con movilización en menos de 4 horas. Los fines de semana y festivos pueden tener un pequeño suplemento en el precio, que siempre se indica claramente en el presupuesto. Podemos trabajar en horario nocturno si la situación lo requiere y las normas de la comunidad lo permiten.'
    },
    {
      category: 'proceso',
      question: '¿Qué información necesitan para hacer un presupuesto?',
      answer: 'Para elaborar un presupuesto preciso necesitamos: dirección y superficie aproximada del inmueble, tiempo aproximado transcurrido desde el fallecimiento, descripción general del estado (si hay afectación de suelos, paredes, muebles), si hay olores persistentes, si se requiere vaciado de enseres, y si hay plazos específicos para la entrega. Si puede enviarnos fotografías del espacio afectado, mejor. Con esta información podemos hacer una valoración telefónica en menos de 2 horas. Si prefiere una valoración presencial, nos desplazamos al inmueble sin compromiso. El presupuesto siempre se entrega por escrito con el desglose completo de servicios y costes.'
    },
    {
      category: 'tecnico',
      question: '¿Emiten certificados oficiales de desinfección?',
      answer: 'Sí, emitimos certificados oficiales de desinfección cuando se requieren para administraciones, nuevos inquilinos o procesos de venta/alquiler. El certificado incluye: identificación de la empresa y número de registro, fecha y lugar de la intervención, productos utilizados (con números de registro sanitario), técnicas aplicadas, y firma del responsable técnico. También proporcionamos documentación fotográfica del antes y después si se solicita. Estos certificados son válidos para presentar ante comunidades de vecinos, administraciones públicas o cualquier otra entidad que los requiera. La emisión del certificado está incluida en el precio del servicio.'
    }
  ];

  useSEO({
    title: 'Preguntas Frecuentes - Limpieza Post Mortem Barcelona | BIOTRAUMA',
    description: 'Resuelva sus dudas sobre limpieza post mortem en Barcelona: precios, plazos, discreción, proceso y más. Respuestas claras a preguntas frecuentes.',
    keywords: 'preguntas frecuentes, FAQ, limpieza post mortem, precios, plazos, Barcelona',
    canonical: '/faq',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        },
        getBreadcrumbSchema([
          { name: 'Inicio', url: '/' },
          { name: 'Preguntas Frecuentes', url: '/faq' }
        ])
      ]
    }
  });

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'todas' || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-light to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6"
          >
            <span className="text-primary font-semibold text-sm">Preguntas Frecuentes</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display font-bold text-5xl lg:text-6xl text-dark mb-6 leading-tight"
          >
            Preguntas frecuentes
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-10"
          >
            Respuestas claras y directas a las preguntas más habituales sobre nuestros servicios de <strong>limpieza tras fallecimiento</strong> en Barcelona. <Link to="/servicios" className="text-primary hover:underline cursor-pointer">Vea todos nuestros servicios especializados</Link>.
          </motion.p>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative max-w-2xl mx-auto"
          >
            <input
              type="text"
              placeholder="Buscar pregunta..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-6 py-4 pl-14 bg-white border-2 border-gray-200 rounded-full text-lg focus:outline-none focus:border-primary transition-colors"
            />
            <i className="ri-search-line absolute left-5 top-1/2 transform -translate-y-1/2 text-2xl text-gray-400"></i>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-primary transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            )}
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex space-x-4 mx-auto">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-6 py-3 rounded-full font-medium whitespace-nowrap transition-all duration-300 cursor-pointer flex items-center space-x-2 ${
                    activeCategory === category.id
                      ? 'bg-primary text-white shadow-lg shadow-primary/30'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <i className={`${category.icon} text-lg`}></i>
                  <span>{category.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {filteredFaqs.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <i className="ri-question-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="font-display font-semibold text-2xl text-gray-600 mb-2">
                No se encontraron resultados
              </h3>
              <p className="text-gray-500 mb-8">
                Intente con otros términos de búsqueda o categoría
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setActiveCategory('todas');
                }}
                className="px-6 py-3 bg-primary text-white font-medium rounded-full hover:bg-primary/90 transition-colors cursor-pointer"
              >
                Ver todas las preguntas
              </button>
            </motion.div>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-light transition-colors cursor-pointer"
                  >
                    <h3 className="font-display font-semibold text-lg text-dark pr-8">
                      {faq.question}
                    </h3>
                    <i className={`ri-arrow-down-s-line text-2xl text-primary transition-transform duration-300 flex-shrink-0 ${
                      openFaq === index ? 'transform rotate-180' : ''
                    }`}></i>
                  </button>
                  
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-8 pb-6 pt-2">
                          <p className="text-gray-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}

          {/* Results Counter */}
          {filteredFaqs.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center mt-12 text-gray-500"
            >
              Mostrando {filteredFaqs.length} de {faqs.length} preguntas
            </motion.div>
          )}
        </div>
      </section>

      {/* Still Have Questions Section */}
      <section className="py-20 lg:py-32 bg-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display font-bold text-4xl lg:text-5xl text-dark mb-6">
                ¿Tiene más preguntas?
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Si no ha encontrado la respuesta que buscaba, no dude en contactarnos. Nuestro equipo está disponible 24/7 para atenderle y resolver cualquier consulta sobre nuestros servicios de <strong>limpieza post mortem en Barcelona</strong>. <Link to="/empresa" className="text-primary hover:underline cursor-pointer">Conozca más sobre nuestra empresa</Link>.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Ofrecemos valoraciones gratuitas y sin compromiso. Podemos realizar una evaluación telefónica con fotografías o desplazarnos al inmueble si lo prefiere. Le proporcionaremos un presupuesto detallado por escrito con toda la información que necesite. <Link to="/servicios" className="text-primary hover:underline cursor-pointer">Vea todos nuestros servicios especializados</Link>.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-primary text-2xl mt-0.5 w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-gray-700">Respuesta inmediata a su consulta</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-primary text-2xl mt-0.5 w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-gray-700">Valoración gratuita sin compromiso</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-primary text-2xl mt-0.5 w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-gray-700">Presupuesto detallado por escrito</span>
                </li>
                <li className="flex items-start space-x-3">
                  <i className="ri-check-line text-primary text-2xl mt-0.5 w-6 h-6 flex items-center justify-center"></i>
                  <span className="text-gray-700">Asesoramiento profesional personalizado</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 lg:p-10 shadow-2xl"
            >
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-2-line text-4xl text-primary"></i>
                </div>
                <h3 className="font-display font-semibold text-2xl text-dark mb-2">
                  Contacte con Nosotros
                </h3>
                <p className="text-gray-600">
                  Disponibles 24/7 para atenderle
                </p>
              </div>

              <div className="space-y-4">
                <a
                  href="tel:+34629501941"
                  className="flex items-center justify-center w-full px-6 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line text-xl mr-3"></i>
                  Llamar: +34 629 501 941
                </a>
                <a
                  href="https://wa.me/34629501941?text=Hola,%20tengo%20una%20consulta%20sobre%20limpieza%20post%20mortem"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-full px-6 py-4 bg-white text-primary font-semibold rounded-full border-2 border-primary hover:bg-primary hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-xl mr-3"></i>
                  WhatsApp
                </a>
                <a
                  href="mailto:info@postmorten.biotrauma.es"
                  className="flex items-center justify-center w-full px-6 py-4 bg-gray-100 text-dark font-semibold rounded-full hover:bg-gray-200 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line text-xl mr-3"></i>
                  Email
                </a>
                <Link
                  to="/contacto"
                  className="flex items-center justify-center w-full px-6 py-4 bg-dark text-white font-semibold rounded-full hover:bg-dark/90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Formulario de Contacto
                  <i className="ri-arrow-right-line text-xl ml-3"></i>
                </Link>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                  <i className="ri-time-line text-primary"></i>
                  <span>Respuesta en menos de 2 horas</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-3xl text-dark mb-4">
              También Puede Interesarle
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link
              to="/servicios"
              className="group bg-light rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <i className="ri-service-line text-2xl text-primary group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="font-display font-semibold text-xl text-dark mb-3 group-hover:text-primary transition-colors">
                Nuestros Servicios
              </h3>
              <p className="text-gray-600 mb-4">
                Conozca en detalle todos nuestros servicios especializados de limpieza post mortem
              </p>
              <span className="text-primary font-medium flex items-center">
                Ver servicios
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
              </span>
            </Link>

            <Link
              to="/empresa"
              className="group bg-light rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <i className="ri-building-line text-2xl text-primary group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="font-display font-semibold text-xl text-dark mb-3 group-hover:text-primary transition-colors">
                Sobre Nosotros
              </h3>
              <p className="text-gray-600 mb-4">
                Conozca nuestra empresa, valores y compromiso con la profesionalidad y discreción
              </p>
              <span className="text-primary font-medium flex items-center">
                Conocer más
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
              </span>
            </Link>

            <Link
              to="/contacto"
              className="group bg-light rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <i className="ri-map-pin-line text-2xl text-primary group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="font-display font-semibold text-xl text-dark mb-3 group-hover:text-primary transition-colors">
                Contacto
              </h3>
              <p className="text-gray-600 mb-4">
                Solicite una valoración sin compromiso o consulte cualquier duda
              </p>
              <span className="text-primary font-medium flex items-center">
                Contactar
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <CookieBanner />
    </div>
  );
};

export default FaqPage;
